import {
  formatDate,
  formatDateTime,
  formatMoney,
  formatRelative,
  formatTime,
  zoneLabel,
  type EffectiveDisplay,
  type Locale,
} from '@ly360/i18n';
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Time display policy (spec 11A): every timestamp renders in the effective zone (user override, else
 * organisation default). Components never format dates themselves (lint forbids toLocale*String).
 */
const DisplayContext = createContext<EffectiveDisplay | null>(null);

export function DisplayProvider({ value, children }: { value: EffectiveDisplay; children: ReactNode }) {
  return <DisplayContext.Provider value={value}>{children}</DisplayContext.Provider>;
}

export function useDisplay(): EffectiveDisplay {
  const d = useContext(DisplayContext);
  if (!d) throw new Error('useDisplay outside DisplayProvider');
  return d;
}

export function useFormat() {
  const { i18n } = useTranslation();
  const d = useDisplay();
  const locale = i18n.language as Locale;
  return useMemo(() => {
    const p = {
      locale,
      calendar: d.calendar,
      digits: d.numerals,
      timeZone: d.timezone,
      timeFormat: d.timeFormat,
      dateStyle: d.dateStyle,
    };
    return {
      timezone: d.timezone,
      /** e.g. "GMT+4 · Asia/Dubai", for table headers and report captions. */
      zoneCaption: d.timezone === 'UTC' ? 'UTC' : `${zoneLabel(d.timezone)} · ${d.timezone}`,
      date: (v: string | Date) => formatDate(new Date(v), p),
      /** Day and month only (birthdays, yearly occasions). */
      dayMonth: (v: string | Date) => formatDate(new Date(v), p, { day: 'numeric', month: 'long' }),
      dateTime: (v: string | Date) => formatDateTime(new Date(v), p),
      dateTimeZoned: (v: string | Date) => formatDateTime(new Date(v), { ...p, showZone: true }),
      time: (v: string | Date) => formatTime(new Date(v), p),
      timeZoned: (v: string | Date) => formatTime(new Date(v), { ...p, showZone: true }),
      relative: (v: string | Date) => formatRelative(new Date(v), new Date(), p),
      money: (minor: number, currency: string) => formatMoney(minor, currency, p),
      number: (n: number) =>
        new Intl.NumberFormat(
          `${locale}-u-nu-${d.numerals === 'native' && (locale === 'ar' || locale === 'ur') ? 'arab' : 'latn'}`,
        ).format(n),
    };
  }, [locale, d]);
}
